import React from "react"
import PropTypes from "prop-types"
import HelpWidgetChartInteractiveMolecule from "views/molecules/interactive/HelpWidgetChartInteractiveMolecule"
import { useTranslation } from "react-i18next"
import {
  TextChart,
  TextChartMinor,
} from "components/widgets/ScorePage/widgets/TextCharts"
import Unit from "views/atoms/reports/Unit"
import NumberFormatterApp from "services/apps/numberFormatterApp"
import { EMISSION_UNIT } from "utils/constants/numbers"
import ChartBadge from "views/atoms/badges/ChartBadge"
import CountryApp from "services/apps/countryApp"
import ChartOdd13ExternalAppEmissions from "views/molecules/canvases/charts/ChartOdd13ExternalAppEmissions"
import ScoreLayoutOrganism from "views/organisms/scores/ScoreLayoutOrganism"
import TextChartOdd13GhgEmissionTab from "views/molecules/canvases/charts/TextChartODD13GHGEmissionTab"
import TCO2e from "views/atoms/units/TCO2e"
import KGCO2e from "views/atoms/units/KGCO2e"
import GCO2e from "views/atoms/units/GCO2e"
import DonutChartTemplate from "views/molecules/charts/template/DonutChartTemplate"
import BarChartTemplate from "views/molecules/charts/template/BarChartTemplate"
import { convertValuesToReadable } from "services/units/valueConversion"
import { CO2_EMISSION_UNITS, T_CO2E } from "services/units/units"
import { round } from "services/units/round"
import { useAppContext } from "components/AppContext"
import ChartWithBadgeWrapperMolecule from "views/molecules/charts/ChartWithBadgeWrapperMolecule"

const SaasAnalysisOrganism = ({ score, saas }) => {
  const { t } = useTranslation()
  const { score: globalScore } = useAppContext()
  const tPrefix = "analyze:it_domain.consolidated.saas"

  const maturityScore = score?.odd13_external_apps_maturity ?? 0

  const chart1TrendBadge = globalScore?.trend_badges?.odd13_software_CO2_external_apps
  const chart2TrendBadge = globalScore?.trend_badges?.odd13_saas_co2_per_employee
  const chart5EmissionFactorTrendBadge = globalScore?.trend_badges?.odd13_saas_emission_factor
  const chart5EmissionsPerAppTrendBadge = globalScore?.trend_badges?.odd13_saas_emissions_per_app
  const maturityTrendBadge = globalScore?.trend_badges?.odd13_external_apps_maturity

  const units = {
    t: <TCO2e />,
    tCO2e: <TCO2e />,
    tSimple: t(`units:units.emission.t_unit`),
    kg: <KGCO2e />,
    g: <GCO2e />,
    gCO2e: <GCO2e />,
    kgCO2e: <KGCO2e />,
    gb: t(`units:units.storage.gb_unit`),
  }
  units.gPerGb = (
    <>
      <Unit unit={NumberFormatterApp.getI18nUnit(EMISSION_UNIT, "g_unit")} />{" "}
      {t(`${tPrefix}.per`)} {units.gb}
    </>
  )

  const perFte = (baseUnit) => (
    <>
      {baseUnit} {t(`${tPrefix}.per_fte`)}
    </>
  )

  const perGb = (baseUnit) => (
    <>
      {baseUnit} {t(`${tPrefix}.per_gb`)}
    </>
  )

  const [
    totalEmissions,
    totalEmissionsUnitEnum,
  ] = NumberFormatterApp.getNumberFormatter(
    Number(saas.CO2_emissions.value) * 1000,
    "emission",
    "kg_unit"
  )
  const totalEmissionsUnit = (
    <Unit
      unit={NumberFormatterApp.getI18nUnit(
        EMISSION_UNIT,
        totalEmissionsUnitEnum
      )}
    />
  )

  const statsForAppFunction = (appFunction, trendBadge) => {
    const localPrefix = `${tPrefix}.${appFunction}`
    const stats = saas[appFunction]

    const content = {
      kpi: round(stats.emissions_per_fte, 0),
      kpi_unit: perFte(
        units[stats.emissions_per_fte_unit] || stats.emissions_per_fte_unit
      ),
      kpi_text: t(`${localPrefix}.kpi_text`),
      sub_kpi: stats.emission_factor,
      sub_kpi_unit: units.gPerGb,
      reference: (
        <ChartBadge
          value={stats.emission_factor}
          lowerLimit={2}
          upperLimit={3}
          inverse
        />
      ),
    }

    return (
      <HelpWidgetChartInteractiveMolecule text={t(`${localPrefix}.help`)}>
        <ChartWithBadgeWrapperMolecule trendBadge={trendBadge} color="auto">
          <TextChart content={<TextChartMinor content={content} />} />
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }

  const averageEmissions = (trendBadge) => {
    const localPrefix = `${tPrefix}.average_CO2`
    const stats = saas.average_CO2

    const content = {
      kpi: round(stats.per_fte, 0),
      kpi_unit: perFte(units[stats.unit]),
      kpi_text: t(`${localPrefix}.title`),
    }

    return (
      <HelpWidgetChartInteractiveMolecule text={t(`${localPrefix}.help`)}>
        <ChartWithBadgeWrapperMolecule trendBadge={trendBadge} color="auto">
          <TextChart content={<TextChartMinor content={content} />} />
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }

  const averageEmissionFactor = (trendBadge) => {
    const localPrefix = `${tPrefix}.emission_factor`
    const { value, unit } = saas.emission_factor

    const content = {
      kpi: value,
      kpi_unit: perGb(units[unit]),
      kpi_text: t(`${localPrefix}.title`),
    }

    return (
      <HelpWidgetChartInteractiveMolecule text={t(`${localPrefix}.help`)}>
        <ChartWithBadgeWrapperMolecule trendBadge={trendBadge} color="auto">
          <TextChart content={<TextChartMinor content={content} />} />
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }

  const emissionsPerApp = (trendBadge) => {
    const localPrefix = `${tPrefix}.emissions_per_app`
    const { value, unit } = saas.emissions_per_application

    const content = {
      kpi: value ? value : "N/A",
      kpi_unit: value ? t(`units:units.emission_per_app.${unit}`) : undefined,
      kpi_text: t(`${localPrefix}.title`),
    }

    return (
      <HelpWidgetChartInteractiveMolecule text={t(`${localPrefix}.help`)}>
        <ChartWithBadgeWrapperMolecule trendBadge={trendBadge} color="auto">
          <TextChart content={<TextChartMinor content={content} />} />
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }

  const chart3 = () => {
    const localPrefix = `${tPrefix}.locations`

    const data = saas.locations.map(({ label, data }) => ({
      data,
      label: label.length === 2 ? CountryApp.getNameI18n(label) : label,
    }))

    const transformedData = data.reduce(
      (acc, { label, data }) => {
        acc.labels.push(label)
        acc.data.push(data)
        return acc
      },
      { labels: [], data: [] }
    )

    return (
      <DonutChartTemplate
        title={t(`${localPrefix}.title`)}
        data={transformedData.data}
        labelKeys={transformedData.labels}
        unitPath={"units:units.emission"}
        unitKey={totalEmissionsUnitEnum}
        helpText={t(`${localPrefix}.help`)}
      />
    )
  }

  const chart4 = () => {
    const localPrefix = `${tPrefix}.providers`

    const convertedData = convertValuesToReadable(
      saas.providers.map(({ data }) => data),
      T_CO2E,
      CO2_EMISSION_UNITS
    )
    const labels = saas.providers.map(({ label }) => label)

    const roundedValues = convertedData.values.map((value) => round(value, 0))

    return (
      <BarChartTemplate
        title={t(`${localPrefix}.title`)}
        data={roundedValues}
        labelKeys={labels}
        unitPath={"units:units.emission"}
        unitKey={convertedData.unit}
        helpText={t(`${localPrefix}.help`)}
        cutLabels={{ maxLength: 15, visibleChars: 15 }}
      />
    )
  }

  return (
    <ScoreLayoutOrganism
      maturity={maturityScore}
      maturityTrendBadge={maturityTrendBadge}
      chart1={
        <TextChartOdd13GhgEmissionTab
          ghgEmissions={saas.CO2_emissions.value}
          ghgEmissionsCollect={score?.odd13_CO2}
          hidePercentage={!score?.should_diplay_percentage}
          trendBadge={chart1TrendBadge}
        />
      }
      chart2={saas.mail ? statsForAppFunction("mail", chart2TrendBadge) : averageEmissions(chart2TrendBadge)}
      chart3={chart3()}
      chart4={chart4()}
      chart5={
        saas.collab
          ? statsForAppFunction("collab", chart2TrendBadge)
          : saas.emission_factor.value !== null
          ? averageEmissionFactor(chart5EmissionFactorTrendBadge)
          : emissionsPerApp(chart5EmissionsPerAppTrendBadge)
      }
      chart6={<ChartOdd13ExternalAppEmissions score={saas} />}
    />
  )
}

const summaryPropType = PropTypes.shape({
  emissions_per_fte: PropTypes.number.isRequired,
  emissions_per_fte_unit: PropTypes.string.isRequired,
  emission_factor: PropTypes.number.isRequired,
  badge: PropTypes.string.isRequired,
})

SaasAnalysisOrganism.propTypes = {
  score: PropTypes.object.isRequired,
  saas: PropTypes.shape({
    score: PropTypes.number.isRequired,
    CO2_emissions: PropTypes.shape({
      value: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
    }).isRequired,
    mail: summaryPropType.isRequired,
    collab: summaryPropType.isRequired,
    average_CO2: PropTypes.shape({
      per_fte: PropTypes.number.isRequired,
      unit: PropTypes.string.isRequired,
    }).isRequired,
    emission_factor: PropTypes.number.isRequired,
    locations: PropTypes.array.isRequired,
    applications: PropTypes.object.isRequired,
    providers: PropTypes.object.isRequired,
  }).isRequired,
  trendBadges: PropTypes.object,
}

SaasAnalysisOrganism.defaultProps = {
  trendBadges: {},
}

export default SaasAnalysisOrganism